import { useCallback, useEffect } from 'react';
import { Loader2, TriangleAlert, Info, FileWarning } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { useAgreements as useLegacyContext } from '@/context/AgreementsContext';
import { useAgreements } from '../hooks/useAgreements';
import { useActiveAgreements } from '../hooks/useActiveAgreements';
import { useAgreementStore } from '../store/agreementStore';
import { AgreementSelector } from '../components/AgreementSelector';
import type { Agreement } from '../types';

const globalWithNotify = globalThis as typeof globalThis & {
  pushNotification?: (type: string, message: string) => void;
};

function triggerPushNotification(type: string, message: string): void {
  globalWithNotify.pushNotification?.(type, message);
}

export default function AgreementsPage() {
  const { data: agreements = [], isLoading, isError, error, refetch } = useAgreements();
  const activeAgreements = useActiveAgreements(agreements);

  const { activeAgreement, setActiveAgreement } = useAgreementStore();

  // Bridge: legacy pages (Punchcard, Pleas, etc.) still read from AgreementsContext.
  // This call keeps AgreementsContext.activeAgreement in sync until those pages are migrated.
  const { setActiveAgreement: bridgeLegacyContext } = useLegacyContext();

  const navigate = useNavigate();
  const auth = useAuth();

  const selectAgreement = useCallback(
    (agreement: Agreement) => {
      setActiveAgreement(agreement);
      bridgeLegacyContext(agreement);
    },
    [setActiveAgreement, bridgeLegacyContext],
  );

  // Auto-select when exactly one active agreement and none is stored.
  useEffect(() => {
    if (isLoading || activeAgreement) return;
    if (activeAgreements.length === 1) {
      selectAgreement(activeAgreements[0]);
    }
  }, [isLoading, activeAgreement, activeAgreements, selectAgreement]);

  // Navigate once a full agreement is selected.
  useEffect(() => {
    if (activeAgreement) {
      navigate('/home');
    }
  }, [activeAgreement, navigate]);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-[60vh] items-center justify-center"
      >
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-label="Carregando acordos" />
      </div>
    );
  }

  if (isError) {
    const message = error instanceof Error ? error.message : 'Erro ao buscar acordos';
    triggerPushNotification('error', message);
    return (
      <main className="space-y-4 px-4 py-6" id="main-content" role="main" aria-label="Conteúdo principal">
        <div
          role="alert"
          aria-live="assertive"
          className="flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
          <p className="text-sm text-red-800">{message}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => refetch()} aria-label="Tentar novamente">
            Tentar novamente
          </Button>
          <Button
            variant="outline"
            onClick={() => auth.signoutRedirect()}
            aria-label="Sair da aplicação"
            className="border-red-200 text-red-700 hover:bg-red-50"
          >
            Sair
          </Button>
        </div>
      </main>
    );
  }

  if (agreements.length === 0) {
    return (
      <main className="space-y-4 px-4 py-6" id="main-content" role="main" aria-label="Conteúdo principal">
        <div
          role="alert"
          aria-live="polite"
          className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="text-sm text-amber-800">
            Nenhum acordo encontrado. Por favor, entre em contato com o suporte técnico.
          </p>
        </div>
      </main>
    );
  }

  if (activeAgreements.length === 0) {
    return (
      <main className="space-y-4 px-4 py-6" id="main-content" role="main" aria-label="Conteúdo principal">
        <div
          role="alert"
          aria-live="polite"
          className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
          <p className="text-sm text-blue-800">Nenhum acordo ativo disponível.</p>
        </div>
      </main>
    );
  }

  if (activeAgreements.length > 1) {
    return <AgreementSelector agreements={activeAgreements} onSelect={selectAgreement} />;
  }

  return null;
}
